import LogoutApi from '@/api/post/logout';
import Card from '@/components/card/card-component';
import Loader from '@/components/loader/loader-component';
import { useMutation } from '@/hooks/use-api';
import { APP_CONTEXT } from '@/utils/context';
import { SmallTitle, Subtitle2Text } from '@/utils/global-fonts';
import { useContext, useEffect, useRef, useState } from 'react';
import { MdLockOutline, MdLogout, MdPersonOutline } from 'react-icons/md';
import theme from '../../common/theme';
import Alert from '../alert/alert-component';
import Styled from './profile-component-style';

/**
 * Profile component.
 * @returns {JSX.Element} The rendered Profile component.
 */
const Profile = () => {
  const [dropdown, setDropdown] = useState(false);
  const popupContainerRef = useRef(null);
  const { currentUser, setCurrentTheme } = useContext(APP_CONTEXT);

  const [logoutUser, { loading: logoutLoading }] = useMutation(LogoutApi);

  const logout = () => {
    logoutUser().then(() => {
      setCurrentTheme(theme);
      Alert({
        icon: 'success',
        text: 'Logout successful.',
      });
      localStorage.removeItem('jwt');
      localStorage.removeItem('superAdmin');
      localStorage.removeItem('permissions');
      setTimeout(() => {
        document.location.replace('/login');
      }, 0);
    });
  };

  const toggleDropdown = () => {
    setDropdown((prevDropdown) => !prevDropdown);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupContainerRef.current &&
        !popupContainerRef.current.contains(event.target)
      ) {
        setDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Styled.ProfileSection ref={popupContainerRef} onClick={toggleDropdown}>
      <Styled.Main>
        <Subtitle2Text>
          {currentUser && !logoutLoading ? (
            `${currentUser?.first_name} ${currentUser?.last_name}`
          ) : (
            <Loader loading size={25} />
          )}
        </Subtitle2Text>
        <Styled.DropdownIcon size={24} cursor="pointer" $active={dropdown} />
      </Styled.Main>
      {dropdown && currentUser && (
        <Styled.DropDown>
          <Card
            className="card"
            width="200px"
            margin="6px 0px 0px 0px"
            borderRadius="4px 4px 4px 4px"
            padding="0px 0px 0px 0px"
            bodyStyle={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              borderTop: 'none',
            }}
          >
            <Styled.DropDownHeading>
              <SmallTitle>My Account</SmallTitle>
            </Styled.DropDownHeading>
            <Styled.OptionNavlink
              to={
                currentUser?.user_type === 'company'
                  ? '/shop/profile-management/profile'
                  : '/app/profile-management/profile'
              }
            >
              <MdPersonOutline />
              <Subtitle2Text>My profile</Subtitle2Text>
            </Styled.OptionNavlink>
            <Styled.OptionNavlink
              to={
                currentUser?.user_type === 'company'
                  ? '/shop/profile-management/profile/change-password'
                  : '/app/profile-management/profile/change-password'
              }
            >
              <MdLockOutline />
              <Subtitle2Text>Update Password</Subtitle2Text>
            </Styled.OptionNavlink>
            <Styled.OptionNavlink onClick={() => logout()}>
              <MdLogout />
              <Subtitle2Text>Sign out</Subtitle2Text>
            </Styled.OptionNavlink>
          </Card>
        </Styled.DropDown>
      )}
    </Styled.ProfileSection>
  );
};

export default Profile;
